import classNames from '@utils/classnames';
import React, { useCallback, useEffect, useMemo } from 'react';
import { ArrowUpIcon, ArrowDownIcon, CaretUpDownIcon } from '@phosphor-icons/react';
import { resolvePath, useLocation, useNavigate } from 'react-router-dom';

import { Checkbox } from '../checkbox/Checkbox';
import { useSelectionReducer } from './selection-reducer';
import { Spinner } from '../Spinner';
import { Delay } from '../Delay';
import { ErrorState } from '../error-state/ErrorState';

export type TableSortOrder = 'asc' | 'desc';

export type TableRow = React.ReactNode[];

export interface ITableHeader {
  id: string;
  name: string;
  width?: string;
  sortable?: boolean;
  hideable?: boolean;
  defaultHidden?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface ITableCallbacks {
  resetSelections: () => void;
  selectAll: () => void;
}

export interface ITableProps<V> {
  idKey: string;
  refreshToken?: string;
  headers: ITableHeader[];
  mapData: (item: V) => TableRow;
  onSelect?: (selectedItems: V[]) => void;
  isMultiSelect?: boolean;
  data: V[];
  hoveredId?: string | number | null;
  onHover?: (id: null | string | number) => void;
  registerCallbacks?: (callbacks: ITableCallbacks) => void;
  sortBy?: string;
  sortOrder?: TableSortOrder;
  onSortChange?: (id: string | undefined, order: TableSortOrder | undefined) => void;
  columnOrder?: string[];
  hiddenColumns?: Set<string>;
  emptyState?: React.ReactNode;
  error?: unknown;
  onRetry?: () => void | Promise<unknown>;
  errorState?: React.ReactNode;
  isLoading?: boolean;
  loadingState?: React.ReactNode;
  className?: string;
  /**
   * Where a click on the row navigates to. When omitted the row follows its
   * first link (normally the identifier in the first column), so list pages
   * get clickable rows without extra wiring.
   */
  rowHref?: (item: V) => string | undefined;
  /** Custom row click handler; takes precedence over `rowHref`. */
  onRowClick?: (item: V, evt: React.MouseEvent<HTMLTableRowElement>) => void;
  /** Set to `false` to turn row clicks off entirely. Default `true`. */
  isRowClickable?: boolean;
}

/** Controls that keep their own behaviour, even while rows are being selected. */
const CONTROL_SELECTOR =
  'button, input, select, textarea, label, summary, [role="button"], [role="menuitem"], [role="checkbox"], [role="switch"], [data-no-row-click]';

/** Clicks that start on one of these do their own thing and never trigger row navigation. */
const INTERACTIVE_SELECTOR = `a, ${CONTROL_SELECTOR}`;

function isModifiedClick(evt: React.MouseEvent) {
  return evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey;
}

interface VisibleColumn {
  header: ITableHeader;
  originalIndex: number;
}

const ALIGN_CLASS: Record<NonNullable<ITableHeader['align']>, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

/**
 * Data table — light header, quiet rows, one border colour. Renders inside a
 * bordered, rounded container; when placed directly in a `.card` the container
 * border is dropped automatically so the table sits flush.
 */
export function Table<V = any>(props: ITableProps<V>) {
  const {
    headers,
    data,
    mapData,
    onSelect,
    registerCallbacks,
    hoveredId,
    onHover,
    idKey,
    sortBy,
    sortOrder,
    onSortChange,
    columnOrder,
    hiddenColumns,
    emptyState,
    error,
    onRetry,
    errorState,
    isLoading,
    loadingState,
    className,
    rowHref,
    onRowClick,
    isRowClickable = true,
  } = props;
  const [{ selectedItems }, dispatchSelection] = useSelectionReducer(Boolean(props.isMultiSelect));
  const navigate = useNavigate();
  const location = useLocation();

  const hasSelection = Boolean(onSelect);
  /**
   * While a multi-select is running the whole row is a selection target: a click that
   * misses the checkbox must not navigate away and drop the selection.
   */
  const isSelecting = hasSelection && Boolean(props.isMultiSelect) && selectedItems.length > 0;

  /** Resolve the row's target: explicit `rowHref`, else the first link rendered in the row. */
  const getRowTarget = useCallback(
    (row: HTMLTableRowElement, item: V): string | null => {
      const explicit = rowHref?.(item);
      if (explicit) {
        const resolved = resolvePath(explicit, location.pathname);
        return resolved.pathname + resolved.search + resolved.hash;
      }
      return row.querySelector<HTMLAnchorElement>('a[href]')?.getAttribute('href') ?? null;
    },
    [rowHref, location.pathname],
  );

  const handleRowClick = useCallback(
    (evt: React.MouseEvent<HTMLTableRowElement>, item: V) => {
      if (!isRowClickable || evt.defaultPrevented) return;
      // Links, buttons, checkboxes… inside the row keep their own behaviour.
      if ((evt.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) return;
      // Don't hijack a text selection.
      // eslint-disable-next-line no-restricted-globals
      if (window.getSelection()?.toString()) return;

      if (onRowClick) {
        onRowClick(item, evt);
        return;
      }

      const href = getRowTarget(evt.currentTarget, item);
      if (!href) return;

      if (isModifiedClick(evt)) {
        // eslint-disable-next-line no-restricted-globals
        window.open(href, '_blank', 'noopener');
      } else {
        navigate(href);
      }
    },
    [isRowClickable, onRowClick, getRowTarget, navigate],
  );

  /** Middle-click opens the row in a new tab, like a link would. */
  const handleRowAuxClick = useCallback(
    (evt: React.MouseEvent<HTMLTableRowElement>, item: V) => {
      if (!isRowClickable || onRowClick || evt.button !== 1) return;
      if ((evt.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) return;
      const href = getRowTarget(evt.currentTarget, item);
      if (href) {
        evt.preventDefault();
        // eslint-disable-next-line no-restricted-globals
        window.open(href, '_blank', 'noopener');
      }
    },
    [isRowClickable, onRowClick, getRowTarget],
  );

  useEffect(() => {
    dispatchSelection({
      type: 'change_type',
      isMultiSelect: Boolean(props.isMultiSelect),
    });
  }, [props.isMultiSelect]);

  useEffect(() => {
    if (onSelect) {
      onSelect(selectedItems.map((v) => data[v]));
    }
  }, [selectedItems, Boolean(onSelect)]);

  const resetSelections = useCallback(() => {
    dispatchSelection({ type: 'reset' });
  }, [dispatchSelection]);

  const selectAll = useCallback(() => {
    dispatchSelection({ type: 'select_all', count: data.length });
  }, [dispatchSelection, data.length]);

  useEffect(() => {
    registerCallbacks?.({
      resetSelections,
      selectAll,
    });
  }, [registerCallbacks, resetSelections, selectAll]);

  const handleSelect = useCallback(
    (index: number) => {
      dispatchSelection({
        type: 'select',
        index,
      });
    },
    [dispatchSelection],
  );

  /**
   * While a multi-select is in progress the row click belongs to the selection:
   * this runs before the row's own links (capture phase) and swallows the click.
   * Only the row's own controls — the checkbox, action buttons, menus — keep their
   * behaviour, since they would be unreachable otherwise.
   */
  const handleRowClickCapture = useCallback(
    (evt: React.MouseEvent<HTMLTableRowElement>, rowIdx: number) => {
      if (!isSelecting) return;
      if ((evt.target as HTMLElement).closest(CONTROL_SELECTOR)) return;

      evt.preventDefault();
      handleSelect(rowIdx);
    },
    [isSelecting, handleSelect],
  );

  const visibleColumns: VisibleColumn[] = useMemo(() => {
    const ordered = columnOrder
      ? columnOrder
          .map((id) => {
            const idx = headers.findIndex((h) => h.id === id);
            return idx >= 0 ? { header: headers[idx], originalIndex: idx } : null;
          })
          .filter((v): v is VisibleColumn => v !== null)
      : headers.map((header, idx) => ({ header, originalIndex: idx }));

    const orderedIds = new Set(ordered.map((v) => v.header.id));
    const extras = headers
      .map((header, idx) => ({ header, originalIndex: idx }))
      .filter((v) => !orderedIds.has(v.header.id));

    const all = [...ordered, ...extras];

    if (!hiddenColumns || hiddenColumns.size === 0) {
      return all;
    }

    return all.filter((v) => !hiddenColumns.has(v.header.id));
  }, [headers, columnOrder, hiddenColumns]);

  const columnCount = visibleColumns.length + (hasSelection ? 1 : 0);
  // Sticky so the header stays visible when the table scrolls inside a fixed-height container.
  const headerCellClassName =
    'sticky top-0 z-10 h-[38px] whitespace-nowrap border-b border-border bg-surface-sunken px-3 text-left';

  return (
    <div className={classNames('table-container overflow-auto rounded-lg border border-border bg-surface', className)}>
      <table className="simple-table w-full text-sm" onMouseLeave={() => onHover?.(null)}>
        <thead>
          <tr>
            {hasSelection && <th className={classNames(headerCellClassName, 'w-10 px-0')}></th>}

            {visibleColumns.map((col) => {
              const header = col.header;
              const isSortable = Boolean(header.sortable && onSortChange);
              const isActive = isSortable && sortBy === header.id;
              const alignClass = ALIGN_CLASS[header.align ?? 'left'];

              const handleClick = () => {
                if (!isSortable || !onSortChange) return;
                if (isActive && sortOrder === 'asc') {
                  onSortChange(header.id, 'desc');
                } else if (isActive && sortOrder === 'desc') {
                  onSortChange(undefined, undefined);
                } else {
                  onSortChange(header.id, 'asc');
                }
              };

              return (
                <th
                  className={headerCellClassName}
                  style={{
                    minWidth: header.width ?? undefined,
                  }}
                  key={`table-header-${header.id}`}
                >
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={handleClick}
                      className={classNames(
                        'flex w-full cursor-pointer select-none items-center gap-1 text-xs font-medium transition-colors hover:text-fg',
                        alignClass,
                        { 'text-fg': isActive, 'text-fg-muted': !isActive },
                      )}
                    >
                      {header.name}
                      {isActive && sortOrder === 'asc' ? (
                        <ArrowUpIcon className="h-3 w-3" weight="bold" />
                      ) : isActive && sortOrder === 'desc' ? (
                        <ArrowDownIcon className="h-3 w-3" weight="bold" />
                      ) : (
                        <CaretUpDownIcon className="h-3 w-3 text-border-strong" weight="bold" />
                      )}
                    </button>
                  ) : (
                    <div className={classNames('flex text-xs font-medium text-fg-muted', alignClass)}>
                      {header.name}
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody className="w-full">
          {errorState || error ? (
            <tr>
              <td colSpan={columnCount} className="py-12 text-center">
                {errorState ?? <ErrorState error={error} onRetry={onRetry} variant="table" />}
              </td>
            </tr>
          ) : isLoading && data.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="py-12 text-center">
                {loadingState ?? (
                  <Delay
                    ms={100}
                    after={
                      <span className="inline-flex items-center gap-2 text-sm text-fg-muted">
                        <Spinner size={4} color="default" />
                        Laden...
                      </span>
                    }
                  />
                )}
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="py-12 text-center">
                {emptyState ?? <span className="text-sm text-fg-muted">Geen resultaten gevonden</span>}
              </td>
            </tr>
          ) : (
            data.map((d, rowIdx) => {
              const mapped = mapData(d);
              const rowId = (d as Record<string, unknown>)[idKey] as string | number | undefined;
              const isHovered = hoveredId === rowId;
              const isSelected = selectedItems.includes(rowIdx);
              // Zebra striping: odd rows sit on the sunken surface so long rows stay easy to track.
              const rowBackground = isSelected
                ? 'bg-brand-subtle'
                : isHovered
                  ? 'bg-surface-hover'
                  : rowIdx % 2 === 1
                    ? 'bg-surface-sunken'
                    : 'bg-surface';

              return (
                <tr
                  key={`table-row-${rowId ?? rowIdx}`}
                  className={classNames('border-b border-border transition-colors last:border-b-0', rowBackground, {
                    'hover:bg-surface-hover': !isSelected,
                    // Explicit target → always a pointer; auto mode → only when the row contains a link.
                    'cursor-pointer': isSelecting || (isRowClickable && Boolean(onRowClick || rowHref)),
                    'table-row-linkable': !isSelecting && isRowClickable && !onRowClick && !rowHref,
                  })}
                  onMouseEnter={() => onHover?.(rowId ?? null)}
                  onClickCapture={(evt) => handleRowClickCapture(evt, rowIdx)}
                  onClick={(evt) => handleRowClick(evt, d)}
                  onAuxClick={(evt) => handleRowAuxClick(evt, d)}
                >
                  {hasSelection && (
                    <td className="h-11 w-10 px-0">
                      <label className="flex h-full w-full cursor-pointer items-center justify-center">
                        <Checkbox onChange={() => handleSelect(rowIdx)} isChecked={isSelected} />
                      </label>
                    </td>
                  )}

                  {visibleColumns.map((col) => {
                    const cell = mapped[col.originalIndex];
                    const alignClass = ALIGN_CLASS[col.header.align ?? 'left'];

                    return (
                      <td key={`table-${rowIdx}-${col.header.id}`} className="h-11 px-3 text-fg">
                        <div className={classNames('flex flex-wrap items-center', alignClass)}>{cell}</div>
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
